import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { WishlistItem } from '../types';
import { getWishlist, addToWishlist as apiAdd, removeFromWishlist as apiRemove } from '../api/wishlist';
import { useAuth } from './AuthContext';

/**
 * Global wishlist state so EVERY product card reflects the user's wishlist —
 * including after login or a hard reload. Previously each card derived its
 * heart from the static `product.is_wishlisted` (always false in the pre-built
 * catalogue), so already-wishlisted items showed an empty heart on reload.
 *
 * This fetches the wishlist whenever the auth `user` changes (login/logout) and
 * on mount (reload), and exposes a set of wishlisted product ids + toggle.
 */
interface WishlistContextType {
  items: WishlistItem[];
  wishlistedIds: Set<number>;
  isWishlisted: (productId: number) => boolean;
  toggleWishlist: (productId: number) => Promise<void>;
  refreshWishlist: () => Promise<void>;
}

const WishlistContext = createContext<WishlistContextType | null>(null);

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be inside WishlistProvider');
  return ctx;
};

export const WishlistProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [items, setItems] = useState<WishlistItem[]>([]);

  const refreshWishlist = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) { setItems([]); return; }
    const list = await getWishlist();
    setItems(list);
  }, []);

  // Refetch on mount (covers hard reload) and whenever the user changes
  // (covers logging in/out — the listing's hearts update without a manual reload).
  useEffect(() => { refreshWishlist(); }, [refreshWishlist, user]);

  const wishlistedIds = new Set(items.map((w) => w.product_id));
  const isWishlisted = useCallback((productId: number) => wishlistedIds.has(productId), [items]);

  const toggleWishlist = useCallback(async (productId: number) => {
    const existing = items.find((w) => w.product_id === productId);
    if (existing) {
      // Optimistic remove, then call the API.
      setItems((prev) => prev.filter((w) => w.product_id !== productId));
      const ok = await apiRemove(existing.wishlist_id);
      if (!ok) await refreshWishlist(); // revert to server truth on failure
    } else {
      const ok = await apiAdd(productId);
      // The POST doesn't return the new row; re-sync from the server so we get
      // the wishlist_id needed for a later remove.
      if (ok) await refreshWishlist();
    }
  }, [items, refreshWishlist]);

  return (
    <WishlistContext.Provider value={{ items, wishlistedIds, isWishlisted, toggleWishlist, refreshWishlist }}>
      {children}
    </WishlistContext.Provider>
  );
};
